// The camera rig. A pose is a position, a target and the rectangle at the
// target that must stay visible; the field of view follows from the viewport
// aspect, so one authored pose frames the same content on any screen.
//
// In the overview the visitor may also orbit, zoom and, by zooming toward a
// point, pan. Those offsets live on top of the authored pose, inside the
// limits the room declares, and fade out whenever the rig flies to a focused
// pose so that pose is shown exactly as authored.

import * as THREE from "three";
import type { CameraPose, RoomManifest } from "../room/contract.ts";
import { ease, type Tweens } from "./tween.ts";

export interface Pose {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fit: THREE.Vector2;
}

export function poseFrom(spec: CameraPose): Pose {
  return {
    position: new THREE.Vector3().fromArray(spec.position),
    target: new THREE.Vector3().fromArray(spec.target),
    fit: new THREE.Vector2().fromArray(spec.fit),
  };
}

interface Offsets {
  azimuth: number;
  elevation: number;
  zoom: number;
  pan: THREE.Vector3;
}

const clamp = THREE.MathUtils.clamp;

export class CameraRig {
  readonly camera = new THREE.PerspectiveCamera(30, 1, 0.05, 80);
  private pose: Pose = { position: new THREE.Vector3(0, 1, 5), target: new THREE.Vector3(), fit: new THREE.Vector2(4, 3) };
  private sway = new THREE.Vector2();
  private swayGoal = new THREE.Vector2();
  /** How far the pointer swings the camera, as a fraction of the view distance. */
  swayAmount = 0.012;
  private aspect = 1;
  /** Where the visitor has put the view, and what is shown as it eases there. */
  private goal: Offsets = { azimuth: 0, elevation: 0, zoom: 1, pan: new THREE.Vector3() };
  private shown: Offsets = { azimuth: 0, elevation: 0, zoom: 1, pan: new THREE.Vector3() };
  /** 1 while the visitor's offsets apply (the overview), 0 on a focused pose. */
  private freedom = 0;
  private limits: RoomManifest["orbit"] = { azimuth: [0, 0], elevation: [0, 0], zoom: [1, 1] };
  private panLimit = new THREE.Vector3();

  constructor(private tweens: Tweens) {}

  resize(aspect: number): void {
    this.aspect = aspect;
    this.apply();
  }

  /** The room's limits on the visitor's view, and how far a pan may carry the target. */
  setLimits(limits: RoomManifest["orbit"], bounds: THREE.Box3): void {
    this.limits = limits;
    this.panLimit.copy(bounds.getSize(new THREE.Vector3())).multiplyScalar(0.42);
    this.reset();
  }

  /** Cut to `pose`. `free` says whether the visitor's own view applies there. */
  set(pose: Pose, free = false): void {
    this.tweens.cancel(this);
    this.pose = { position: pose.position.clone(), target: pose.target.clone(), fit: pose.fit.clone() };
    this.freedom = free ? 1 : 0;
    this.apply();
  }

  /** Move to `pose` along a gentle arc. `free` says whether the visitor's own view applies there. */
  fly(pose: Pose, duration = 900, free = false): Promise<void> {
    const from = { position: this.pose.position.clone(), target: this.pose.target.clone(), fit: this.pose.fit.clone() };
    const lift = Math.min(0.35, from.position.distanceTo(pose.position) * 0.06);
    const freedom = this.freedom;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    return this.tweens.run(reduced ? 0 : duration, (t) => {
      this.pose.position.lerpVectors(from.position, pose.position, t);
      this.pose.position.y += Math.sin(t * Math.PI) * lift;
      this.pose.target.lerpVectors(from.target, pose.target, t);
      this.pose.fit.lerpVectors(from.fit, pose.fit, t);
      this.freedom = THREE.MathUtils.lerp(freedom, free ? 1 : 0, t);
    }, ease.inOut, this);
  }

  /** Pointer position in -1..1 on both axes. */
  pointer(x: number, y: number): void {
    this.swayGoal.set(x, y);
  }

  /** Turn the view by a pointer drag of (dx, dy) CSS pixels: the room follows the hand. */
  orbit(dx: number, dy: number): void {
    const g = this.goal;
    g.azimuth = clamp(g.azimuth - dx * 0.0045, this.limits.azimuth[0], this.limits.azimuth[1]);
    g.elevation = clamp(g.elevation + dy * 0.0035, this.limits.elevation[0], this.limits.elevation[1]);
  }

  /**
   * Zoom by `factor` (below 1 is closer) toward the point under (x, y), given
   * in -1..1. Zooming in carries the target toward that point; zooming back
   * out returns it, so the overview is centred again at zoom 1.
   */
  zoom(factor: number, x: number, y: number): void {
    const g = this.goal;
    const next = clamp(g.zoom * factor, this.limits.zoom[0], this.limits.zoom[1]);
    if (next === g.zoom) return;
    if (next < g.zoom) {
      const centre = this.pose.target.clone().add(g.pan);
      const normal = this.camera.getWorldDirection(new THREE.Vector3());
      const ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2(x, -y), this.camera);
      const hit = ray.ray.intersectPlane(new THREE.Plane().setFromNormalAndCoplanarPoint(normal, centre), new THREE.Vector3());
      if (hit) g.pan.addScaledVector(hit.sub(centre), 1 - next / g.zoom);
    } else {
      g.pan.multiplyScalar(g.zoom < 1 ? Math.max(0, (1 - next) / (1 - g.zoom)) : 0);
    }
    g.pan.clamp(this.panLimit.clone().negate(), this.panLimit);
    g.zoom = next;
  }

  /** Back to the authored overview. */
  reset(): void {
    this.goal = { azimuth: 0, elevation: 0, zoom: 1, pan: new THREE.Vector3() };
  }

  /** Ease toward the pointer and the visitor's view; true while still moving. */
  update(dt: number): boolean {
    const k = 1 - Math.exp(-dt / 220);
    const q = 1 - Math.exp(-dt / 110);
    const before = this.camera.position.clone();
    this.sway.lerp(this.swayGoal, k);
    this.shown.azimuth += (this.goal.azimuth - this.shown.azimuth) * q;
    this.shown.elevation += (this.goal.elevation - this.shown.elevation) * q;
    this.shown.zoom += (this.goal.zoom - this.shown.zoom) * q;
    this.shown.pan.lerp(this.goal.pan, q);
    this.apply();
    return before.distanceToSquared(this.camera.position) > 1e-10;
  }

  private apply(): void {
    const f = this.freedom;
    const target = this.pose.target.clone().addScaledVector(this.shown.pan, f);
    const arm = new THREE.Vector3().subVectors(this.pose.position, this.pose.target);
    const zoom = 1 + (this.shown.zoom - 1) * f;
    // Orbit: the arm from target to camera, as an azimuth around Y and an elevation.
    const length = arm.length();
    const azimuth = Math.atan2(arm.x, arm.z) + this.shown.azimuth * f;
    const elevation = clamp(Math.asin(arm.y / length) + this.shown.elevation * f, -1.4, 1.4);
    arm.set(Math.sin(azimuth) * Math.cos(elevation), Math.sin(elevation), Math.cos(azimuth) * Math.cos(elevation)).multiplyScalar(length * zoom);
    const position = target.clone().add(arm);
    const fit = this.pose.fit.clone().multiplyScalar(zoom);

    const distance = arm.length();
    const forward = arm.clone().multiplyScalar(-1 / distance);
    const right = new THREE.Vector3().crossVectors(forward, THREE.Object3D.DEFAULT_UP).normalize();
    const up = new THREE.Vector3().crossVectors(right, forward);
    const reach = distance * this.swayAmount;
    this.camera.position.copy(position)
      .addScaledVector(right, this.sway.x * reach)
      .addScaledVector(up, -this.sway.y * reach * 0.6);
    this.camera.lookAt(target);
    const half = Math.max(fit.y / 2, fit.x / (2 * this.aspect));
    this.camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(half / distance));
    this.camera.aspect = this.aspect;
    this.camera.near = Math.max(0.02, distance * 0.02);
    this.camera.updateProjectionMatrix();
  }
}
